import { useEffect, useState, useCallback, useMemo } from "react";
import { api, fileUrl } from "@/lib/api";
import { Inbox, Phone, Mail, MapPin, Plus, Trash2, FileText, Loader2, Send, ExternalLink, UserCog } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = ["reviewing", "enroute", "on_site", "quoted", "sold"];
const STATUS_LABEL = { reviewing: "Reviewing", enroute: "En route", on_site: "On site", quoted: "Quoted", sold: "Sold" };
const fmtUsd = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

function QuoteListItem({ quote, isActive, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(quote)}
      className={`flex w-full flex-col gap-1 rounded-md border-l-2 px-3 py-3 text-left transition-colors ${isActive ? "border-l-blue-600 bg-blue-50/60" : "border-l-transparent hover:bg-slate-50"}`}
      data-testid={`quote-list-item-${quote.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-slate-900 truncate">{quote.name}</div>
        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-blue-800">{STATUS_LABEL[quote.status] || quote.status}</span>
      </div>
      <div className="text-xs text-slate-500 truncate">{quote.project_type}</div>
      <div className="text-xs text-slate-400">{new Date(quote.created_at).toLocaleString()}</div>
    </button>
  );
}

function QuoteDetailRow({ k, v }) {
  return (
    <div className="flex gap-3"><span className="w-28 shrink-0 text-slate-500">{k}</span><span className="text-slate-900">{v}</span></div>
  );
}

function QuoteDetail({ quote, onStatusChange, onInvoiced, onAssign, onDelete, assignees }) {
  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900">{quote.name}</h2>
          <div className="mt-1 flex flex-wrap gap-3 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {quote.phone}</span>
            <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {quote.email}</span>
            <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {quote.address}</span>
          </div>
          {quote.netvendor_ref && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-800" data-testid={`quote-netvendor-ref-${quote.id}`}>
              <span className="font-semibold uppercase tracking-wider">NetVendor</span>
              <span className="text-indigo-900">{quote.netvendor_ref}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <Select value={quote.status} onValueChange={(v) => onStatusChange(quote.id, v)}>
            <SelectTrigger className="w-40" data-testid={`quote-status-select-${quote.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={quote.assigned_user_id || "unassigned"} onValueChange={(v) => onAssign(quote.id, v === "unassigned" ? null : v)}>
            <SelectTrigger className="w-44" data-testid={`quote-assign-select-${quote.id}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {assignees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email} <span className="text-[10px] uppercase text-slate-400 ml-1">({u.role})</span></SelectItem>)}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={() => onDelete(quote)}
            className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 transition-colors hover:bg-rose-50"
            data-testid={`quote-delete-button-${quote.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete quote
          </button>
        </div>
      </div>
      <div className="mt-5 grid gap-2 text-sm">
        <QuoteDetailRow k="Project" v={quote.project_type} />
        <QuoteDetailRow k="Pool type" v={quote.pool_type || "—"} />
        <QuoteDetailRow k="Submitted" v={new Date(quote.created_at).toLocaleString()} />
      </div>
      {quote.address && (
        <div className="mt-5">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Location</div>
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(quote.address)}`} target="_blank" rel="noreferrer" className="text-xs font-medium text-cyan-700 hover:underline" data-testid="quote-open-in-maps-link">Open in Google Maps ↗</a>
          </div>
          <div className="mt-2 overflow-hidden rounded-md border border-slate-200" data-testid="quote-map-embed">
            <iframe
              title={`Map of ${quote.address}`}
              src={`https://maps.google.com/maps?q=${encodeURIComponent(quote.address)}&z=14&output=embed`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="block h-64 w-full"
            />
          </div>
        </div>
      )}
      <div className="mt-5">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Description</div>
        <p className="mt-2 whitespace-pre-wrap rounded-md bg-slate-50 p-4 text-sm text-slate-800">{quote.description}</p>
      </div>
      <div className="mt-5">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Photos ({quote.photo_ids?.length || 0})</div>
        <div className="mt-2 grid grid-cols-3 gap-2 md:grid-cols-4">
          {(quote.photo_ids || []).map((id) => (
            <a key={id} href={fileUrl(id)} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-md border border-slate-200 bg-slate-50">
              <img src={fileUrl(id)} alt="" className="h-24 w-full object-cover transition hover:scale-105" />
            </a>
          ))}
        </div>
      </div>
      <InvoicePanel quote={quote} onInvoiced={onInvoiced} />
    </div>
  );
}

function InvoicePanel({ quote, onInvoiced }) {
  const existingOrderId = quote.invoice_order_id;
  const [open, setOpen] = useState(quote.status === "sold" || quote.status === "quoted");
  const [busy, setBusy] = useState(false);
  const newLine = () => ({ id: crypto.randomUUID(), description: "", quantity: 1, dollars: "" });
  const [items, setItems] = useState(() => [newLine()]);
  const [notes, setNotes] = useState("");

  const total = items.reduce((s, it) => {
    const d = parseFloat(it.dollars);
    return s + (isNaN(d) ? 0 : Math.round(d * 100) * (parseInt(it.quantity, 10) || 1));
  }, 0);

  const addLine = () => setItems((arr) => [...arr, newLine()]);
  const removeLine = (id) => setItems((arr) => (arr.length === 1 ? arr : arr.filter((it) => it.id !== id)));
  const updateLine = (id, k, v) => setItems((arr) => arr.map((it) => (it.id === id ? { ...it, [k]: v } : it)));

  const send = async () => {
    const cleanItems = items
      .map((it) => ({
        description: it.description.trim(),
        quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
        price_cents: Math.round((parseFloat(it.dollars) || 0) * 100),
      }))
      .filter((it) => it.description && it.price_cents > 0);
    if (cleanItems.length === 0) { toast.error("Add at least one valid line item"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/quotes/${quote.id}/invoice`, { line_items: cleanItems, notes });
      toast.success(`Invoice sent to ${quote.email}`);
      onInvoiced?.(quote.id, r.data.order_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invoice");
    } finally { setBusy(false); }
  };

  if (existingOrderId) {
    return (
      <div className="mt-6 rounded-md border border-emerald-200 bg-emerald-50/60 p-5">
        <div className="flex items-center gap-2 text-emerald-800">
          <FileText className="h-4 w-4" /> <span className="font-semibold">Invoice already sent</span>
        </div>
        <p className="mt-1 text-sm text-emerald-900/80">A pay link was emailed and texted to {quote.email}.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <a href={`/pay/${existingOrderId}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700" data-testid="invoice-open-pay-link">
            <ExternalLink className="h-3 w-3" /> Open invoice page
          </a>
          <code className="rounded bg-white/80 px-2 py-1 font-mono text-[11px] text-slate-600">order {existingOrderId.slice(0, 8)}…</code>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-md border border-slate-200 bg-slate-50/60 p-5">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-left" data-testid="invoice-builder-toggle">
        <div className="flex items-center gap-2 text-slate-900">
          <FileText className="h-4 w-4 text-cyan-700" />
          <span className="font-semibold">Build invoice & mark sold</span>
        </div>
        <span className="text-xs text-slate-500">{open ? "Hide" : "Show"}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-slate-500">Add line items below. The customer ({quote.email}) will get an email + SMS with a private pay link.</p>
          <div className="hidden grid-cols-12 gap-2 px-1 text-[11px] font-semibold uppercase tracking-widest text-slate-500 md:grid">
            <div className="col-span-7">Description</div>
            <div className="col-span-2">Qty</div>
            <div className="col-span-2">Price (USD)</div>
            <div className="col-span-1" />
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={it.id} className="grid grid-cols-12 gap-2" data-testid={`invoice-line-${i}`}>
                <Input className="col-span-12 md:col-span-7" placeholder="e.g. Replaster, blue pebble finish" value={it.description} onChange={(e) => updateLine(it.id, "description", e.target.value)} data-testid={`invoice-line-${i}-desc`} />
                <Input className="col-span-3 md:col-span-2" type="number" min={1} value={it.quantity} onChange={(e) => updateLine(it.id, "quantity", e.target.value)} data-testid={`invoice-line-${i}-qty`} />
                <div className="relative col-span-7 md:col-span-2">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">$</span>
                  <Input className="pl-6" type="text" inputMode="decimal" placeholder="0.00" value={it.dollars} onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "" || /^\d*(\.\d{0,2})?$/.test(raw)) updateLine(it.id, "dollars", raw);
                  }} data-testid={`invoice-line-${i}-price`} />
                </div>
                <button type="button" onClick={() => removeLine(it.id)} className="col-span-2 grid place-items-center rounded-md border border-slate-200 bg-white text-slate-500 hover:border-red-300 hover:text-red-500 md:col-span-1" aria-label="Remove line" data-testid={`invoice-line-${i}-remove`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addLine} data-testid="invoice-add-line-button">
            <Plus className="h-4 w-4" /> Add line
          </Button>
          <div>
            <Label>Notes / payment instructions (optional)</Label>
            <Textarea className="mt-1" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Payment due within 14 days. Thanks for your business!" data-testid="invoice-notes-textarea" />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-3">
            <div className="text-sm text-slate-500">Total</div>
            <div className="font-display text-xl font-bold text-slate-900" data-testid="invoice-total">{fmtUsd(total)}</div>
          </div>
          <Button onClick={send} disabled={busy || total <= 0} className="w-full" data-testid="invoice-send-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send invoice & mark sold
          </Button>
        </div>
      )}
    </div>
  );
}

export default function QuotesInbox() {
  const { user: me } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("all"); // all | mine | unassigned
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [q, u] = await Promise.all([api.get("/quotes"), api.get("/users").catch(() => ({ data: [] }))]);
      setQuotes(q.data);
      setUsers(u.data.filter((x) => ["admin", "owner"].includes(x.role)));
      setSelected((cur) => {
        if (!cur) return q.data.length ? q.data[0] : null;
        const fresh = q.data.find((x) => x.id === cur.id);
        return fresh || (q.data.length ? q.data[0] : null);
      });
    } catch {
      toast.error("Failed to load quotes");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (tab === "mine") return quotes.filter((q) => q.assigned_user_id === me?.user_id);
    if (tab === "unassigned") return quotes.filter((q) => !q.assigned_user_id);
    return quotes;
  }, [quotes, tab, me]);

  const counts = useMemo(() => ({
    all: quotes.length,
    mine: quotes.filter((q) => q.assigned_user_id === me?.user_id).length,
    unassigned: quotes.filter((q) => !q.assigned_user_id).length,
  }), [quotes, me]);

  const updateStatus = useCallback(async (id, status) => {
    try {
      await api.patch(`/quotes/${id}/status`, { status });
      setQuotes((q) => q.map((x) => (x.id === id ? { ...x, status } : x)));
      setSelected((s) => (s?.id === id ? { ...s, status } : s));
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
    }
  }, []);

  const assignQuote = useCallback(async (id, userId) => {
    try {
      const r = await api.patch(`/quotes/${id}/assign`, { user_id: userId || null });
      setQuotes((q) => q.map((x) => (x.id === id ? r.data : x)));
      setSelected((s) => (s?.id === id ? r.data : s));
      toast.success(userId ? "Quote assigned" : "Unassigned");
    } catch (e) { toast.error(e.response?.data?.detail || "Assignment failed"); }
  }, []);

  const onInvoiced = useCallback((quoteId, orderId) => {
    setQuotes((q) => q.map((x) => (x.id === quoteId ? { ...x, status: "sold", invoice_order_id: orderId } : x)));
    setSelected((s) => (s?.id === quoteId ? { ...s, status: "sold", invoice_order_id: orderId } : s));
  }, []);

  const onConfirmDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/quotes/${confirmDelete.id}`);
      setQuotes((q) => q.filter((x) => x.id !== confirmDelete.id));
      setSelected((s) => (s?.id === confirmDelete.id ? null : s));
      toast.success(`Deleted quote from ${confirmDelete.name}`);
      setConfirmDelete(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-slate-900">Quote requests</h1>
      <p className="mt-1 text-sm text-slate-500">{quotes.length} total — newest first</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {[
          { key: "all", label: "All" },
          { key: "mine", label: "Assigned to me" },
          { key: "unassigned", label: "Unassigned" },
        ].map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition ${active ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"}`}
              data-testid={`quotes-tab-${t.key}`}
            >
              {t.label}
              <span className={`rounded-full px-1.5 text-[11px] font-semibold ${active ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>{counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-12">
        <div className="dash-surface max-h-[70vh] overflow-y-auto p-2 lg:col-span-4">
          {filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              <Inbox className="mx-auto h-6 w-6 text-slate-400" /> No quotes match this filter
            </div>
          )}
          {filtered.map((q) => (
            <QuoteListItem key={q.id} quote={q} isActive={selected?.id === q.id} onSelect={setSelected} />
          ))}
        </div>

        <div className="dash-surface p-6 lg:col-span-8">
          {!selected && <div className="text-slate-500">Select a quote to view details.</div>}
          {selected && <QuoteDetail quote={selected} onStatusChange={updateStatus} onInvoiced={onInvoiced} onAssign={assignQuote} onDelete={setConfirmDelete} assignees={users} />}
        </div>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => { if (!open && !deleting) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="quote-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this quote?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the quote from <strong className="text-slate-900">{confirmDelete?.name}</strong>
              {confirmDelete?.email ? <> ({confirmDelete.email})</> : null} along with all uploaded photos. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="quote-delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); onConfirmDelete(); }}
              disabled={deleting}
              className="bg-rose-600 text-white hover:bg-rose-700 focus-visible:ring-rose-500"
              data-testid="quote-delete-confirm-button"
            >
              {deleting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting…</> : <><Trash2 className="mr-2 h-4 w-4" /> Delete quote</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
